import React, { useEffect, useMemo, useRef, useState } from "react";
import { Spinner, Tooltip } from "@heroui/react";
import { WhatsappLogo, ArrowSquareOut } from "@phosphor-icons/react";
import { useSession, useUser } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";

interface WhatsAppContactSectionProps {
  /** Internal contact id (Contact entity id, not Twilio sid). Required. */
  contactId: number;
  /** Optional friendly name for the section header. */
  contactName?: string;
  /** When false, surfaces a "geen GSM-nummer geregistreerd" hint in the empty state. */
  hasMobile?: boolean;
  /** Restrict shown messages to the last N days. */
  daysFilter?: number;
  /** Cap the number of recent messages rendered inline. Older messages are referred to the dedicated WhatsApp page. */
  maxMessages?: number;
  /** Optional class names applied to the outer wrapper. */
  className?: string;
}

interface WhatsappMessage {
  sid: string;
  author: string;
  body: string;
  dateCreated: string;
  media?: { mediaUrl?: string; content_type?: string }[];
}

interface ConversationResponse {
  conversation: {
    sid: string;
    friendlyName?: string;
    state?: string;
    dateUpdated?: string;
  } | null;
  messages: WhatsappMessage[];
  participants: any[];
  lastReadAt: string | null;
}

const isInboundAuthor = (author: string | undefined | null) => {
  if (!author) return false;
  return author.startsWith("whatsapp:") || /^\+\d+/.test(author);
};

const formatTimestamp = (iso: string) => {
  try {
    return new Date(iso).toLocaleString("nl-BE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export const WhatsAppContactSection: React.FC<WhatsAppContactSectionProps> = ({
  contactId,
  contactName,
  hasMobile = true,
  daysFilter,
  maxMessages = 5,
  className = "",
}) => {
  const { session } = useSession();
  const { user } = useUser();

  const [data, setData] = useState<ConversationResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Initial data load
  useEffect(() => {
    if (!session || !contactId) {
      setData(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setErrorMessage(null);
      try {
        const url = new URL(`${API_URL}/whatsapp/by-contact/${contactId}`);
        if (user?.id) url.searchParams.set("userId", user.id);

        const response = await fetchWithAuth(
          url.toString(),
          { method: "GET" },
          session,
        );
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || "Onbekende fout");
        }
        const json: ConversationResponse = await response.json();
        if (!cancelled) setData(json);
      } catch (err: any) {
        console.error("Error loading WhatsApp conversation for contact", err);
        if (!cancelled) {
          setErrorMessage(
            "Kon WhatsApp gesprek niet laden. Probeer het later opnieuw.",
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [contactId, session, user?.id]);

  const conversationSid = data?.conversation?.sid;

  // Mark conversation as read silently when this read-only view mounts
  useEffect(() => {
    if (!conversationSid || !session || !user?.id) return;
    const markRead = async () => {
      try {
        await fetchWithAuth(
          `${API_URL}/whatsapp/conversations/${conversationSid}/mark-read`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userId: user.id }),
          },
          session,
        );
        window.dispatchEvent(new CustomEvent("whatsapp:unread-changed"));
      } catch (err) {
        console.error("Error marking WhatsApp conversation as read", err);
      }
    };
    markRead();
  }, [conversationSid, session, user?.id]);

  const lastReadDate = useMemo(() => {
    if (!data?.lastReadAt) return null;
    return new Date(data.lastReadAt);
  }, [data?.lastReadAt]);

  const sortedMessages = useMemo(() => {
    if (!data?.messages) return [];
    return [...data.messages].sort(
      (a, b) =>
        new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime(),
    );
  }, [data?.messages]);

  const cutoffDate = useMemo(() => {
    if (!daysFilter || daysFilter <= 0) return null;
    const d = new Date();
    d.setDate(d.getDate() - daysFilter);
    return d;
  }, [daysFilter]);

  const messagesInWindow = useMemo(() => {
    if (!cutoffDate) return sortedMessages;
    return sortedMessages.filter(
      (m) => new Date(m.dateCreated).getTime() >= cutoffDate.getTime(),
    );
  }, [sortedMessages, cutoffDate]);

  const visibleMessages = useMemo(() => {
    if (!maxMessages || maxMessages <= 0) return messagesInWindow;
    return messagesInWindow.slice(-maxMessages);
  }, [messagesInWindow, maxMessages]);

  const olderHiddenCount = Math.max(
    0,
    messagesInWindow.length - visibleMessages.length,
  );

  // Auto-scroll to the latest visible message on first render
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [visibleMessages.length]);

  if (!contactId) return null;

  const headerSubject = contactName || "deze contactpersoon";
  const conversationLink = conversationSid
    ? `/conversations/${conversationSid}`
    : null;

  return (
    <div
      className={`overflow-hidden rounded-lg border border-neutral-primary/15 bg-white shadow-sm ${className}`}
    >
      {/* WhatsApp-style header */}
      <div
        className="flex items-center justify-between gap-2 border-b border-neutral-primary/10 px-3 py-2"
        style={{ backgroundColor: "#f0f2f5" }}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
            style={{ backgroundColor: "#25d366" }}
          >
            <WhatsappLogo size={18} weight="fill" className="text-white" />
          </div>
          <div className="min-w-0">
            <div className="text-neutral-primary truncate text-sm font-semibold leading-tight">
              WhatsApp · {headerSubject}
            </div>
            {daysFilter ? (
              <div className="text-neutral-secondary-mid text-[11px] leading-tight">
                Laatste {daysFilter} dagen
              </div>
            ) : null}
          </div>
        </div>
        {conversationLink && (
          <Tooltip content="Open volledig gesprek in WhatsApp pagina">
            <a
              href={conversationLink}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-moss-primary hover:bg-moss-primary/90 inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium text-white"
            >
              <WhatsappLogo size={12} weight="fill" />
              Open
              <ArrowSquareOut size={10} weight="bold" />
            </a>
          </Tooltip>
        )}
      </div>

      <div className="flex flex-col">
        {isLoading && (
          <div className="flex items-center justify-center py-10">
            <Spinner size="sm" />
          </div>
        )}

        {!isLoading && errorMessage && (
          <div className="text-neutral-secondary-mid px-4 py-6 text-sm">
            {errorMessage}
          </div>
        )}

        {!isLoading && !errorMessage && !data?.conversation && (
          <div className="text-neutral-secondary-mid flex flex-col items-center gap-2 px-4 py-10 text-center text-sm">
            <WhatsappLogo
              size={28}
              weight="duotone"
              className="text-neutral-secondary-mid/60"
            />
            <span>
              Geen WhatsApp gesprek gevonden voor {headerSubject}
              {hasMobile ? "" : " (geen GSM-nummer geregistreerd)"}.
            </span>
          </div>
        )}

        {!isLoading &&
          data?.conversation &&
          messagesInWindow.length === 0 &&
          (cutoffDate ? (
            <div className="text-neutral-secondary-mid flex flex-col items-center gap-2 px-4 py-10 text-center text-sm">
              <WhatsappLogo
                size={28}
                weight="duotone"
                className="text-neutral-secondary-mid/60"
              />
              <span>
                Geen WhatsApp berichten in de laatste {daysFilter} dagen.
              </span>
              {conversationLink && (
                <a
                  href={conversationLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-moss-primary inline-flex items-center gap-1 text-xs hover:underline"
                >
                  Bekijk volledig gesprek
                  <ArrowSquareOut size={12} />
                </a>
              )}
            </div>
          ) : (
            <div
              className="text-neutral-secondary-mid flex flex-col items-center px-4 py-10 text-center text-sm"
              style={{ backgroundColor: "#efeae2" }}
            >
              Nog geen berichten
            </div>
          ))}

        {!isLoading && data?.conversation && visibleMessages.length > 0 && (
          <div
            className="flex h-[460px] flex-col space-y-1.5 overflow-y-auto px-3 py-3"
            style={{ backgroundColor: "#efeae2" }}
          >
            {olderHiddenCount > 0 && conversationLink && (
              <a
                href={conversationLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-moss-primary mx-auto inline-flex items-center gap-1 self-center rounded-full bg-white/80 px-3 py-1 text-[11px] font-medium shadow-sm hover:bg-white"
              >
                Bekijk {olderHiddenCount} ouder{olderHiddenCount === 1 ? "" : "e"} bericht
                {olderHiddenCount === 1 ? "" : "en"} in WhatsApp
                <ArrowSquareOut size={11} weight="bold" />
              </a>
            )}
            {visibleMessages.map((message) => {
              const inbound = isInboundAuthor(message.author);
              const created = new Date(message.dateCreated);
              const isUnread =
                inbound && (!lastReadDate || created > lastReadDate);
              return (
                <div
                  key={message.sid}
                  className={`flex ${inbound ? "justify-start" : "justify-end"}`}
                >
                  <div className="max-w-[78%]">
                    <div
                      className={`relative rounded-lg px-3 py-2 text-sm shadow-sm ${
                        inbound
                          ? "bg-white text-neutral-primary rounded-tl-none"
                          : "text-neutral-primary rounded-tr-none"
                      }`}
                      style={
                        inbound ? undefined : { backgroundColor: "#d9fdd3" }
                      }
                    >
                      {message.body && (
                        <div className="whitespace-pre-wrap break-words">
                          {message.body}
                        </div>
                      )}
                      {message.media && message.media.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {message.media.map((m, idx) =>
                            m.mediaUrl &&
                            m.content_type?.startsWith("image/") ? (
                              <img
                                key={idx}
                                src={m.mediaUrl}
                                alt="Bijlage"
                                className="max-h-32 rounded border"
                              />
                            ) : m.mediaUrl ? (
                              <a
                                key={idx}
                                href={m.mediaUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-moss-primary text-xs underline"
                              >
                                Bijlage openen
                              </a>
                            ) : null,
                          )}
                        </div>
                      )}
                      <div className="text-neutral-secondary-mid mt-1 flex items-center justify-end gap-2 text-[11px]">
                        <span>{formatTimestamp(message.dateCreated)}</span>
                        {isUnread && (
                          <span className="bg-moss-primary inline-flex items-center rounded-full px-2 py-[1px] text-[10px] font-medium text-white">
                            Ongelezen
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Read-only footer reminding the user to use the dedicated page for replies */}
        {!isLoading && data?.conversation && conversationLink && (
          <div
            className="border-t border-neutral-primary/10 flex items-center justify-between gap-2 px-3 py-1.5 text-[11px]"
            style={{ backgroundColor: "#f0f2f5" }}
          >
            <span className="text-neutral-secondary-mid truncate">
              Antwoorden via WhatsApp pagina
            </span>
            <a
              href={conversationLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-moss-primary inline-flex shrink-0 items-center gap-1 font-medium hover:underline"
            >
              Open gesprek
              <ArrowSquareOut size={11} weight="bold" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppContactSection;
